/**
 * Picker for adding a non-winery "place" stop (restaurant, hotel, park, etc.).
 * Type-ahead against `trpc.places.searchPlaces` (Google establishment search);
 * on selection, `trpc.places.geocode` resolves coordinates + address, which are
 * snapshotted onto the stop. Mirrors VenuePickerModal's modal shell.
 *
 * Google billing rule: one session token spans the autocomplete keystrokes +
 * the geocode that closes the session — rotated after each successful pick.
 */

import { useEffect, useRef, useState } from 'react';
import { trpc } from '../../lib/trpc';
import { Heading4, BodyText, Caption } from '../ui/Typography';
import { Button } from '../ui/Button';

export interface PickedPlace {
  placeId: string;
  name: string;
  address?: string;
  lat: number;
  lng: number;
}

interface PlacePickerModalProps {
  title: string;
  /** Optional point to bias results toward (e.g. the day's last place stop). */
  bias?: { lat: number; lng: number };
  onPick: (place: PickedPlace) => void;
  onClose: () => void;
}

export const PlacePickerModal = ({
  title,
  bias,
  onPick,
  onClose,
}: PlacePickerModalProps) => {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionTokenRef = useRef<string>(crypto.randomUUID());
  const utils = trpc.useUtils();

  const trimmed = term.trim();

  // Debounce the query sent to Places autocomplete (200ms — matches RegionSearch).
  useEffect(() => {
    const t = setTimeout(() => setDebounced(trimmed), 200);
    return () => clearTimeout(t);
  }, [trimmed]);

  const search = trpc.places.searchPlaces.useQuery(
    { query: debounced, sessionToken: sessionTokenRef.current, bias },
    { enabled: debounced.length >= 2 }
  );

  const handleBackdrop = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !resolving) onClose();
  };

  const handlePick = async (placeId: string, fallbackName: string) => {
    setResolving(true);
    setError(null);
    try {
      const geo = await utils.places.geocode.fetch({
        placeId,
        sessionToken: sessionTokenRef.current,
      });
      // Rotate the session token after the geocode closes the billing session.
      sessionTokenRef.current = crypto.randomUUID();
      onPick({
        placeId,
        name: geo.name || fallbackName,
        address: geo.address,
        lat: geo.lat,
        lng: geo.lng,
      });
    } catch {
      setError("Couldn't load that place. Pick another or try again.");
      setResolving(false);
    }
  };

  const suggestions = search.data?.suggestions ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 p-4 pt-16"
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-full max-w-lg rounded-lg bg-gray-50 shadow-xl">
        <div className="flex items-center justify-between border-b border-[#DBD4C9] p-4">
          <Heading4>{title}</Heading4>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            disabled={resolving}
            aria-label="Close place picker"
          >
            Close
          </Button>
        </div>

        <div className="p-4">
          <input
            type="text"
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && !resolving) onClose();
            }}
            disabled={resolving}
            placeholder="Search restaurants, hotels, parks…"
            className="w-full rounded-lg border border-[#DBD4C9] bg-gray-50 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info-500 disabled:opacity-60"
            aria-label="Search places"
          />

          <div className="mt-4 max-h-80 overflow-y-auto">
            {error && (
              <Caption className="mb-2 block text-error">{error}</Caption>
            )}
            {resolving && (
              <Caption className="text-gray-500">Adding place…</Caption>
            )}
            {!resolving && (
              <>
                {trimmed.length < 2 && (
                  <Caption className="text-gray-500">
                    Type at least 2 characters to search.
                  </Caption>
                )}
                {trimmed.length >= 2 && search.isLoading && (
                  <Caption className="text-gray-500">Searching…</Caption>
                )}
                {search.isError && (
                  <Caption className="text-error">
                    Couldn&apos;t search places. Try again.
                  </Caption>
                )}
                {search.isSuccess &&
                  suggestions.length === 0 &&
                  debounced.length >= 2 && (
                    <Caption className="text-gray-500">
                      No places match &ldquo;{debounced}&rdquo;.
                    </Caption>
                  )}
                <ul className="space-y-2">
                  {suggestions.map((s) => (
                    <li key={s.placeId}>
                      <button
                        type="button"
                        onClick={() => handlePick(s.placeId, s.label)}
                        className="flex min-h-[44px] w-full flex-col items-start rounded-lg border border-[#DBD4C9] bg-gray-50 px-3 py-2 text-left hover:bg-[#F2F0EA] focus:outline-none focus:ring-2 focus:ring-info-500"
                      >
                        <BodyText className="text-gray-900">{s.label}</BodyText>
                        {s.secondaryLabel && (
                          <Caption className="text-gray-500">
                            {s.secondaryLabel}
                          </Caption>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
